/**
 * REST client for the persisted notification inbox (Phase B.1).
 *
 * Backend: `backend/src/routes/notifications.ts`. The existing
 * `notificationService.ts` is a Socket.IO wrapper for live push and
 * stays as-is; this module is for the REST inbox CRUD.
 */
import { apiService } from './api';

// Phase C.88: tuple + derived-type pattern, mirroring the backend
// `NOTIFICATION_*` tuples in `models/Notification.ts` (Phase C.66).
// Single source of truth for category / priority / type literals on
// the frontend — dropdowns iterate the runtime constant, the union
// type stays in sync automatically. Adding a new category is a
// one-line edit in this file plus the matching backend tuple.
export const INBOX_NOTIFICATION_TYPES = [
  'success',
  'warning',
  'error',
  'info',
] as const;
export type InboxNotificationType = (typeof INBOX_NOTIFICATION_TYPES)[number];

export const INBOX_NOTIFICATION_CATEGORIES = [
  'system',
  'import',
  'project',
  'user',
  'security',
  'infra',
  'ssr',
] as const;
export type InboxNotificationCategory =
  (typeof INBOX_NOTIFICATION_CATEGORIES)[number];

export const INBOX_NOTIFICATION_PRIORITIES = [
  'low',
  'medium',
  'high',
  'critical',
] as const;
export type InboxNotificationPriority =
  (typeof INBOX_NOTIFICATION_PRIORITIES)[number];

// Phase C.75: DTO trimmed to fields the frontend actually consumes.
// Backend returns the full Mongoose doc (with `userId`, `alertId`,
// `readAt`, `updatedAt`, etc.) — those are unused on the client and
// just bloat the type. JSON over the wire still carries them, the
// type just stops promising them as part of the API contract.
export interface InboxNotification {
  _id: string;
  type: InboxNotificationType;
  category: InboxNotificationCategory;
  priority: InboxNotificationPriority;
  title: string;
  message: string;
  read: boolean;
  actionUrl?: string;
  actionLabel?: string;
  projectId?: string;
  jobId?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface InboxListResult {
  items: InboxNotification[];
  nextCursor: string | null;
}

export interface InboxListParams {
  limit?: number;
  before?: string;
  unread?: boolean;
  category?: InboxNotificationCategory;
}

const unwrap = <T>(res: unknown): T => (res as { result: T }).result;

export const fetchInbox = async (
  params: InboxListParams = {}
): Promise<InboxListResult> => {
  const query: Record<string, string | number | boolean> = {};
  if (params.limit) query.limit = params.limit;
  if (params.before) query.before = params.before;
  if (params.unread) query.unread = 'true';
  if (params.category) query.category = params.category;
  const res = await apiService.get<InboxListResult>('/notifications', query);
  return unwrap<InboxListResult>(res);
};

export const fetchUnreadCount = async (): Promise<number> => {
  const res = await apiService.get<{ count: number }>(
    '/notifications/unread-count'
  );
  return unwrap<{ count: number }>(res).count;
};

export const markNotificationRead = async (id: string): Promise<void> => {
  await apiService.patch(`/notifications/${id}/read`);
};

export const markAllNotificationsRead = async (
  params: { category?: InboxNotificationCategory } = {}
): Promise<number> => {
  const res = await apiService.post<{ modifiedCount: number }>(
    '/notifications/mark-all-read',
    params.category ? { category: params.category } : undefined
  );
  return unwrap<{ modifiedCount: number }>(res).modifiedCount;
};

export const deleteNotification = async (id: string): Promise<void> => {
  await apiService.delete(`/notifications/${id}`);
};

export const clearReadNotifications = async (
  params: { category?: InboxNotificationCategory } = {}
): Promise<number> => {
  const res = await apiService.post<{ deletedCount: number }>(
    '/notifications/clear',
    params.category ? { category: params.category } : undefined
  );
  return unwrap<{ deletedCount: number }>(res).deletedCount;
};

/**
 * Phase C.9: fire a self-test notification for the current user.
 * Goes through the same `createNotification` helper as production
 * events, so a successful round trip means DB write + Socket.IO
 * emit + bell update all work end-to-end.
 */
export interface TestNotificationResult {
  id: string;
  /** Phase C.56: ISO timestamp when the diagnostic row auto-deletes. */
  expiresAt: string;
  /** Phase C.73: resolved category / priority echoed back for UI confirm. */
  category: InboxNotificationCategory;
  priority: InboxNotificationPriority;
}

export const sendTestNotification = async (
  options: {
    category?: InboxNotificationCategory;
    priority?: InboxNotificationPriority;
  } = {}
): Promise<TestNotificationResult> => {
  const body: Record<string, string> = {};
  if (options.category) body.category = options.category;
  if (options.priority) body.priority = options.priority;
  const res = await apiService.post<TestNotificationResult>(
    '/notifications/test',
    Object.keys(body).length > 0 ? body : undefined
  );
  return unwrap<TestNotificationResult>(res);
};
